// {unread > 9 ? "9+" : String(unread)}
import React, { useEffect, useState } from 'react';
import { MapView } from '@/components/MapView';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { getAllRoutes, deleteRoute, getRouteById, type Route } from '@/api/routeService';
import { Package, MapPin } from 'lucide-react';
import GlobalLoader from '../../components/ui/GlobalLoader';

export function RouteMapPage() {
  const [routes, setRoutes] = useState<Route[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [selectedRoute, setSelectedRoute] = useState<Route | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'pickup-orders' | 'orders'>('pickup-orders');

  const fetchRoutes = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getAllRoutes({ limit: 50 });
      const d: any = (res as any)?.data;
      const parsed: any[] = Array.isArray(d)
        ? d
        : Array.isArray(d?.routes)
        ? d.routes
        : Array.isArray(d?.data)
        ? d.data
        : Array.isArray(d?.data?.routes)
        ? d.data.routes
        : d
        ? [d]
        : [];
      console.log('🔍 Fetched routes data:', parsed);
      console.log('🔍 First route orders:', parsed[0]?.orders);
      console.log('🔍 First route pickupOrder:', parsed[0]?.pickupOrder);
      console.log('🔍 First route waypoints:', parsed[0]?.waypoints);
      console.log('🔍 First route full object:', parsed[0]);
      setRoutes(parsed as Route[]);
    } catch (err: any) {
      console.error('Error loading routes:', err);
      setError(err?.message || 'Failed to load routes');
    } finally {
      setLoading(false);
    }
  };

  // Filter routes based on active tab
  const getFilteredRoutes = (): Route[] => {
    return routes.filter(route => {
      // If routeType is not set (old routes), show them in pickup-orders tab for backward compatibility
      if (!route.routeType) {
        return activeTab === 'pickup-orders';
      }
      return route.routeType === activeTab;
    });
  };

  useEffect(() => {
    fetchRoutes();
  }, []);

  const handleDelete = async (route: Route) => {
    const id = (route._id || route.id || route.routeId) as string;
    if (!id) return;
    if (!confirm(`Delete route "${route.name || id}"?`)) return;
    
    try {
      setDeletingId(id);
      await deleteRoute(id);
      await fetchRoutes();
    } catch (err) {
      console.error('Failed to delete route', err);
      // alert('Failed to delete route');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCloseModal = () => {
    setViewModalOpen(false);
    setSelectedRoute(null);
  };

  const handleViewRoute = async (route: Route) => {
    try {
      console.log('🔍 Fetching full route details for:', route._id || route.id || route.routeId);
      const routeId = route._id || route.id || route.routeId;
      if (!routeId) {
        console.error('No route ID found');
        setSelectedRoute(route);
        setViewModalOpen(true);
        return;
      }
      
      // Fetch full route details with populated orders
      const fullRouteResponse = await getRouteById(routeId as string);
      console.log('🔍 Full route response:', fullRouteResponse);
      
      if (fullRouteResponse.success && fullRouteResponse.data) {
        console.log('🔍 Full route data with orders:', fullRouteResponse.data);
        const routeData = Array.isArray(fullRouteResponse.data) ? fullRouteResponse.data[0] : fullRouteResponse.data;
        console.log('🔍 Route data orders:', (routeData as any).orders);
        console.log('🔍 Route data pickupOrder:', (routeData as any).pickupOrder);
        console.log('🔍 Route data waypoints:', routeData.waypoints);
        setSelectedRoute(routeData);
      } else {
        console.log('🔍 Using original route data');
        setSelectedRoute(route);
      }
      
      setViewModalOpen(true);
    } catch (error) {
      console.error('Error fetching full route details:', error);
      // Fallback to original route data
      setSelectedRoute(route);
      setViewModalOpen(true);
    }
  };

  const handleTabChange = (value: string) => {
    if (value === 'pickup-orders' || value === 'orders') {
      setActiveTab(value);
    }
  };

  return (
    <div className="h-[70vh] w-full">
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full h-full">
        <TabsList className="grid w-full grid-cols-2 bg-gray-100 p-1 rounded-lg mb-4">
          <TabsTrigger value="pickup-orders" 
          className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-primary/20 transition-all duration-200 hover:bg-white/50" 
          >
            <Package className="w-4 h-4" />
            Pickup Orders
          </TabsTrigger>
          <TabsTrigger value="orders" 
          className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-primary/20 transition-all duration-200 hover:bg-white/50" 
          >
            <MapPin className="w-4 h-4" />
            Orders
          </TabsTrigger>
        </TabsList>

        {/* Pickup Orders Tab */}
        <TabsContent value="pickup-orders" className="h-[70vh] w-full flex">
          {/* Left Sidebar */}
          <div className="w-1/4 h-[80vh] bg-white border-r border-gray-200 flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Create and manage pickup routes</h2>
            </div>
            
            <div className="flex-1 p-6 overflow-y-auto">
              <Card className="mb-4">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Pickup Routes ({getFilteredRoutes().length})</CardTitle>
                    <Button variant="outline" size="sm" onClick={fetchRoutes} disabled={loading}>
                      {loading ? 'Refreshing…' : 'Refresh'}
                    </Button>
                  </div>
                  <p className="text-sm text-gray-500">Click on a route to view on map</p>
                </CardHeader>
                <CardContent>
                  {error ? (
                    <div className="text-red-600 text-sm">{error}</div>
                  ) : getFilteredRoutes().length === 0 ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <p className="text-gray-500 text-sm">No routes created yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {getFilteredRoutes().map((route) => (
                        <div key={route._id || route.routeId || route.id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="font-medium text-sm text-gray-900">{route.name || route.routeId || 'Untitled Route'}</div>
                              <div className="text-xs text-gray-500 mt-1">{route.description || 'No description'}</div>
                            </div>
                            <Badge className={
                              route.status === 'active' ? 'bg-blue-100 text-blue-800' :
                              route.status === 'completed' ? 'bg-green-100 text-green-800' :
                              'bg-gray-100 text-gray-800'
                            }>
                              {route.status?.replace('-', ' ') || 'inactive'}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-3">
                            <div>Waypoints: <span className="font-medium">{route.waypoints?.length || 0}</span></div>
                            <div>Distance: <span className="font-medium">{route.distance ? `${route.distance.toFixed ? route.distance.toFixed(2) : route.distance} km` : '-'}</span></div>
                            <div>ETA: <span className="font-medium">{route.estimatedTime ? `${Math.round(route.estimatedTime)} min` : '-'}</span></div>
                            <div>Created: <span className="font-medium">{route.createdAt ? new Date(route.createdAt).toLocaleDateString() : '-'}</span></div>
                          </div>
                          <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => handleViewRoute(route)}>
                              View
                            </Button>
                            <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(route)} disabled={deletingId === (route._id || route.id || route.routeId)}>
                              {deletingId === (route._id || route.id || route.routeId) ? 'Deleting…' : 'Delete'}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Right Map Area */}
          <div className="flex-1">
            <MapView onRouteCreated={fetchRoutes} mode="pickup-orders" />
          </div>
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders" className="h-[70vh] w-full flex">
          {/* Left Sidebar */}
          <div className="w-1/4 h-[80vh] bg-white border-r border-gray-200 flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Create and manage order routes</h2>
            </div>
            
            <div className="flex-1 p-6 overflow-y-auto">
              <Card className="mb-4">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Order Routes ({getFilteredRoutes().length})</CardTitle>
                    <Button variant="outline" size="sm" onClick={fetchRoutes} disabled={loading}>
                      {loading ? 'Refreshing…' : 'Refresh'}
                    </Button>
                  </div>
                  <p className="text-sm text-gray-500">Click on a route to view on map</p>
                </CardHeader>
                <CardContent>
                  {error ? (
                    <div className="text-red-600 text-sm">{error}</div>
                  ) : getFilteredRoutes().length === 0 ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <p className="text-gray-500 text-sm">No routes created yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {getFilteredRoutes().map((route) => (
                        <div key={route._id || route.routeId || route.id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="font-medium text-sm text-gray-900">{route.name || route.routeId || 'Untitled Route'}</div>
                              <div className="text-xs text-gray-500 mt-1">{route.description || 'No description'}</div>
                            </div>
                            <Badge className={
                              route.status === 'active' ? 'bg-blue-100 text-blue-800' :
                              route.status === 'completed' ? 'bg-green-100 text-green-800' :
                              'bg-gray-100 text-gray-800'
                            }>
                              {route.status?.replace('-', ' ') || 'inactive'}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-3">
                            <div>Waypoints: <span className="font-medium">{route.waypoints?.length || 0}</span></div>
                            <div>Distance: <span className="font-medium">{route.distance ? `${route.distance.toFixed ? route.distance.toFixed(2) : route.distance} km` : '-'}</span></div>
                            <div>ETA: <span className="font-medium">{route.estimatedTime ? `${Math.round(route.estimatedTime)} min` : '-'}</span></div>
                            <div>Created: <span className="font-medium">{route.createdAt ? new Date(route.createdAt).toLocaleDateString() : '-'}</span></div>
                          </div>
                          <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => handleViewRoute(route)}>
                              View
                            </Button>
                            <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(route)} disabled={deletingId === (route._id || route.id || route.routeId)}>
                              {deletingId === (route._id || route.id || route.routeId) ? 'Deleting…' : 'Delete'}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Right Map Area */}
          <div className="flex-1">
            <MapView onRouteCreated={fetchRoutes} mode="orders" />
          </div>
        </TabsContent>
      </Tabs>

      {/* View Route Modal */}
      <Dialog open={viewModalOpen} onOpenChange={handleCloseModal}>
        <DialogContent className="max-w-6xl w-full h-[80vh] bg-white p-0">
          <div className="flex-1 min-h-0">
            <MapView 
              selectedRoute={selectedRoute}
              isViewOnly={true}
              mode={activeTab}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Global loading overlay */}
      <GlobalLoader 
        isLoading={loading}
        message="Loading route data..."
        subMessage="Please wait while we fetch your routes"
      />
    </div>
  );
}

